import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiCheckCircle,
    FiClock,
    FiCreditCard,
    FiEye,
    FiLoader,
    FiRefreshCw,
    FiSearch,
    FiSlash,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";

import {
    config,
} from "../../../../../../utils/apiUrl";

import "./PaymentListPage.css";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(number)
        ? number
        : fallback;
};

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        toNumber(value)
    );

const formatDate = (
    value,
    includeTime = false
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        includeTime
            ? {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            }
            : {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }
    ).format(date);
};

const formatStatus = (
    value
) =>
    String(value || "-")
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(
            /\b\w/g,
            (character) =>
                character.toUpperCase()
        );

const getPaymentId = (
    payment
) =>
    payment?._id ||
    payment?.id ||
    "";

const getPaymentNumber = (
    payment
) =>
    payment?.paymentNumber ||
    payment?.receiptNumber ||
    payment?.referenceNumber ||
    "-";

const getPaymentAmount = (
    payment
) =>
    toNumber(
        payment?.amount ??
        payment?.paymentAmount ??
        payment?.receivedAmount ??
        0
    );

const getAllocatedAmount = (
    payment
) =>
    toNumber(
        payment?.allocatedAmount ??
        payment?.allocationSummary
            ?.allocatedAmount ??
        0
    );

const getAvailableAmount = (
    payment
) =>
    toNumber(
        payment?.availableAmount ??
        payment?.unallocatedAmount ??
        payment?.allocationSummary
            ?.availableAmount ??
        Math.max(
            getPaymentAmount(payment) -
            getAllocatedAmount(
                payment
            ),
            0
        )
    );

const getCustomerName = (
    payment
) =>
    payment?.customerSnapshot
        ?.customerName ||
    payment?.customerSnapshot?.name ||
    payment?.customer?.name ||
    payment?.customerName ||
    "Walk-in Customer";

const getCustomerMobile = (
    payment
) =>
    payment?.customerSnapshot?.mobile ||
    payment?.customer?.mobile ||
    payment?.mobile ||
    "-";

const getStatusIcon = (
    status
) => {
    const normalized =
        String(status || "")
            .toUpperCase();

    if (
        normalized === "COMPLETED" ||
        normalized === "CONFIRMED" ||
        normalized === "POSTED"
    ) {
        return <FiCheckCircle />;
    }

    if (
        normalized === "REVERSED" ||
        normalized === "CANCELLED"
    ) {
        return <FiSlash />;
    }

    return <FiClock />;
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const PaymentListPage = () => {
    const [
        payments,
        setPayments,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(false);

    const [
        error,
        setError,
    ] = useState("");

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        statusFilter,
        setStatusFilter,
    ] = useState("");

    const [
        selectedPayment,
        setSelectedPayment,
    ] = useState(null);

    /*
    |--------------------------------------------------------------------------
    | LOAD
    |--------------------------------------------------------------------------
    */

    const loadPayments =
        useCallback(
            async () => {
                try {
                    setLoading(
                        true
                    );

                    setError(
                        ""
                    );

                    if (
                        !config
                            ?.getBillingPayments
                    ) {
                        throw new Error(
                            "Billing payments API URL is not configured."
                        );
                    }

                    const api =
                        new API();

                    const allPayments =
                        [];

                    const pageLimit =
                        200;

                    let currentPage =
                        1;

                    let hasNextPage =
                        true;

                    while (
                        hasNextPage
                    ) {
                        const query =
                            new URLSearchParams({
                                page:
                                    String(
                                        currentPage
                                    ),

                                limit:
                                    String(
                                        pageLimit
                                    ),
                            });

                        const response =
                            await api.get(
                                `${config.getBillingPayments}?${query.toString()}`
                            );

                        const envelope =
                            response?.data &&
                                (
                                    response.data?.success !==
                                    undefined ||
                                    response.data?.status !==
                                    undefined ||
                                    response.data?.code !==
                                    undefined
                                )
                                ? response.data
                                : response ||
                                {};

                        if (
                            envelope?.success ===
                                false ||
                            envelope?.status ===
                                "error"
                        ) {
                            throw new Error(
                                envelope?.message ||
                                "Unable to load payments."
                            );
                        }

                        const responseData =
                            envelope?.data &&
                                typeof envelope.data ===
                                "object"
                                ? envelope.data
                                : {};

                        const pageRecords =
                            Array.isArray(
                                responseData?.records
                            )
                                ? responseData.records
                                : Array.isArray(
                                    responseData?.payments
                                )
                                    ? responseData.payments
                                    : Array.isArray(
                                        envelope?.records
                                    )
                                        ? envelope.records
                                        : [];

                        allPayments.push(
                            ...pageRecords
                        );

                        const pagination =
                            responseData?.pagination ||
                            envelope?.pagination ||
                            response?.meta ||
                            {};

                        const totalPages =
                            Math.max(
                                toNumber(
                                    pagination?.totalPages,
                                    1
                                ),
                                1
                            );

                        hasNextPage =
                            typeof pagination?.hasNextPage ===
                                "boolean"
                                ? pagination.hasNextPage
                                : currentPage <
                                totalPages;

                        if (
                            !Object.keys(
                                pagination
                            ).length
                        ) {
                            hasNextPage =
                                pageRecords.length ===
                                pageLimit;
                        }

                        currentPage +=
                            1;

                        if (
                            pageRecords.length ===
                                0 ||
                            currentPage >
                                1000
                        ) {
                            hasNextPage =
                                false;
                        }
                    }

                    setPayments(
                        allPayments
                    );
                } catch (
                    loadError
                ) {
                    console.error(
                        "Unable to load payments:",
                        loadError
                    );

                    setPayments(
                        []
                    );

                    setError(
                        loadError?.response?.data?.message ||
                        loadError?.data?.message ||
                        loadError?.message ||
                        "Unable to load payments."
                    );
                } finally {
                    setLoading(
                        false
                    );
                }
            },
            []
        );

    useEffect(
        () => {
            loadPayments();
        },
        [loadPayments]
    );

    /*
    |--------------------------------------------------------------------------
    | FILTER
    |--------------------------------------------------------------------------
    */

    const filteredPayments =
        useMemo(
            () => {
                const keyword =
                    search
                        .trim()
                        .toLowerCase();

                return payments.filter(
                    (payment) => {
                        const status =
                            String(
                                payment?.status ||
                                payment?.paymentStatus ||
                                ""
                            ).toUpperCase();

                        if (
                            statusFilter &&
                            status !==
                            statusFilter
                        ) {
                            return false;
                        }

                        if (!keyword) {
                            return true;
                        }

                        const searchable =
                            [
                                getPaymentNumber(
                                    payment
                                ),
                                getCustomerName(
                                    payment
                                ),
                                getCustomerMobile(
                                    payment
                                ),
                                payment
                                    ?.paymentMethod,
                                payment
                                    ?.paymentMode,
                                payment
                                    ?.referenceNumber,
                                payment
                                    ?.transactionId,
                                payment
                                    ?.saleInvoiceNumber,
                            ]
                                .filter(Boolean)
                                .join(" ")
                                .toLowerCase();

                        return searchable.includes(
                            keyword
                        );
                    }
                );
            },
            [
                payments,
                search,
                statusFilter,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | SUMMARY
    |--------------------------------------------------------------------------
    */

    const summary =
        useMemo(
            () =>
                payments.reduce(
                    (
                        result,
                        payment
                    ) => {
                        result.totalAmount +=
                            getPaymentAmount(
                                payment
                            );

                        result.allocated +=
                            getAllocatedAmount(
                                payment
                            );

                        result.available +=
                            getAvailableAmount(
                                payment
                            );

                        return result;
                    },
                    {
                        totalAmount: 0,
                        allocated: 0,
                        available: 0,
                    }
                ),
            [payments]
        );

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="payment-list-page">
            <header className="payment-list-header">
                <div className="payment-list-heading">
                    <span className="payment-list-heading-icon">
                        <FiCreditCard />
                    </span>

                    <div>
                        <h1>
                            Payments
                        </h1>

                        <p>
                            View customer payments,
                            allocations and available
                            balances.
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    className="payment-list-refresh"
                    disabled={loading}
                    onClick={
                        loadPayments
                    }
                >
                    <FiRefreshCw
                        className={
                            loading
                                ? "payment-list-spinner"
                                : ""
                        }
                    />

                    Refresh
                </button>
            </header>

            <section className="payment-list-summary">
                <div>
                    <span>
                        Total Payments
                    </span>

                    <strong>
                        {payments.length}
                    </strong>
                </div>

                <div>
                    <span>
                        Received
                    </span>

                    <strong>
                        {formatCurrency(
                            summary.totalAmount
                        )}
                    </strong>
                </div>

                <div>
                    <span>
                        Allocated
                    </span>

                    <strong>
                        {formatCurrency(
                            summary.allocated
                        )}
                    </strong>
                </div>

                <div>
                    <span>
                        Available
                    </span>

                    <strong>
                        {formatCurrency(
                            summary.available
                        )}
                    </strong>
                </div>
            </section>

            <section className="payment-list-filters">
                <div className="payment-list-search">
                    <FiSearch />

                    <input
                        type="text"
                        value={search}
                        placeholder="Search payment, customer, mobile, reference..."
                        onChange={(
                            event
                        ) =>
                            setSearch(
                                event.target
                                    .value
                            )
                        }
                    />
                </div>

                <select
                    value={
                        statusFilter
                    }
                    onChange={(
                        event
                    ) =>
                        setStatusFilter(
                            event.target
                                .value
                        )
                    }
                >
                    <option value="">
                        All Status
                    </option>

                    <option value="PENDING">
                        Pending
                    </option>

                    <option value="COMPLETED">
                        Completed
                    </option>

                    <option value="POSTED">
                        Posted
                    </option>

                    <option value="REVERSED">
                        Reversed
                    </option>

                    <option value="CANCELLED">
                        Cancelled
                    </option>
                </select>
            </section>

            {error && (
                <div className="payment-list-error">
                    <FiAlertCircle />

                    {error}
                </div>
            )}

            <section className="payment-list-card">
                {loading &&
                    payments.length === 0 ? (
                    <div className="payment-list-loading">
                        <FiLoader className="payment-list-spinner" />

                        Loading payments...
                    </div>
                ) : filteredPayments.length ===
                    0 ? (
                    <div className="payment-list-empty">
                        <FiCreditCard />

                        <h3>
                            No payments found
                        </h3>

                        <p>
                            Customer payments will
                            appear here.
                        </p>
                    </div>
                ) : (
                    <div className="payment-list-table-wrapper">
                        <table className="payment-list-table">
                            <thead>
                                <tr>
                                    <th>
                                        Payment
                                    </th>

                                    <th>
                                        Date
                                    </th>

                                    <th>
                                        Customer
                                    </th>

                                    <th>
                                        Mode
                                    </th>

                                    <th>
                                        Reference
                                    </th>

                                    <th className="text-right">
                                        Amount
                                    </th>

                                    <th className="text-right">
                                        Allocated
                                    </th>

                                    <th className="text-right">
                                        Available
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th>
                                        Action
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {filteredPayments.map(
                                    (
                                        payment,
                                        index
                                    ) => {
                                        const id =
                                            getPaymentId(
                                                payment
                                            );

                                        const status =
                                            String(
                                                payment
                                                    ?.status ||
                                                payment
                                                    ?.paymentStatus ||
                                                "PENDING"
                                            ).toUpperCase();

                                        return (
                                            <tr
                                                key={
                                                    id ||
                                                    index
                                                }
                                            >
                                                <td>
                                                    <strong>
                                                        {getPaymentNumber(
                                                            payment
                                                        )}
                                                    </strong>
                                                </td>

                                                <td>
                                                    {formatDate(
                                                        payment
                                                            ?.paymentDate ||
                                                        payment
                                                            ?.createdAt,
                                                        true
                                                    )}
                                                </td>

                                                <td>
                                                    <strong>
                                                        {getCustomerName(
                                                            payment
                                                        )}
                                                    </strong>

                                                    <span>
                                                        {getCustomerMobile(
                                                            payment
                                                        )}
                                                    </span>
                                                </td>

                                                <td>
                                                    {formatStatus(
                                                        payment
                                                            ?.paymentMethod ||
                                                        payment
                                                            ?.paymentMode ||
                                                        "-"
                                                    )}
                                                </td>

                                                <td>
                                                    {payment
                                                        ?.referenceNumber ||
                                                        payment
                                                            ?.transactionId ||
                                                        "-"}
                                                </td>

                                                <td className="text-right payment-list-amount">
                                                    {formatCurrency(
                                                        getPaymentAmount(
                                                            payment
                                                        )
                                                    )}
                                                </td>

                                                <td className="text-right">
                                                    {formatCurrency(
                                                        getAllocatedAmount(
                                                            payment
                                                        )
                                                    )}
                                                </td>

                                                <td className="text-right payment-list-available">
                                                    {formatCurrency(
                                                        getAvailableAmount(
                                                            payment
                                                        )
                                                    )}
                                                </td>

                                                <td>
                                                    <span
                                                        className={`payment-list-status payment-list-status-${status.toLowerCase()}`}
                                                    >
                                                        {getStatusIcon(
                                                            status
                                                        )}

                                                        {formatStatus(
                                                            status
                                                        )}
                                                    </span>
                                                </td>

                                                <td>
                                                    <button
                                                        type="button"
                                                        className="payment-list-view"
                                                        onClick={() =>
                                                            setSelectedPayment(
                                                                payment
                                                            )
                                                        }
                                                    >
                                                        <FiEye />

                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    }
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {selectedPayment && (
                <div className="payment-list-modal-overlay">
                    <div className="payment-list-modal">
                        <div className="payment-list-modal-header">
                            <div>
                                <h2>
                                    Payment Details
                                </h2>

                                <p>
                                    {getPaymentNumber(
                                        selectedPayment
                                    )}
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                    setSelectedPayment(
                                        null
                                    )
                                }
                            >
                                ×
                            </button>
                        </div>

                        <div className="payment-list-modal-grid">
                            <div>
                                <span>
                                    Customer
                                </span>

                                <strong>
                                    {getCustomerName(
                                        selectedPayment
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Mobile
                                </span>

                                <strong>
                                    {getCustomerMobile(
                                        selectedPayment
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Payment Date
                                </span>

                                <strong>
                                    {formatDate(
                                        selectedPayment
                                            ?.paymentDate ||
                                        selectedPayment
                                            ?.createdAt,
                                        true
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Payment Mode
                                </span>

                                <strong>
                                    {formatStatus(
                                        selectedPayment
                                            ?.paymentMethod ||
                                        selectedPayment
                                            ?.paymentMode
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Amount
                                </span>

                                <strong>
                                    {formatCurrency(
                                        getPaymentAmount(
                                            selectedPayment
                                        )
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Allocated
                                </span>

                                <strong>
                                    {formatCurrency(
                                        getAllocatedAmount(
                                            selectedPayment
                                        )
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Available
                                </span>

                                <strong>
                                    {formatCurrency(
                                        getAvailableAmount(
                                            selectedPayment
                                        )
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Reference
                                </span>

                                <strong>
                                    {selectedPayment
                                        ?.referenceNumber ||
                                        selectedPayment
                                            ?.transactionId ||
                                        "-"}
                                </strong>
                            </div>
                        </div>

                        <div className="payment-list-modal-footer">
                            <button
                                type="button"
                                onClick={() =>
                                    setSelectedPayment(
                                        null
                                    )
                                }
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentListPage;